import React, { useState, useCallback } from "react";
import {
  TextField,
  MenuItem,
  Grid,
  Paper,
  Typography,
  Autocomplete,
  Button,
} from "@mui/material";
import axios from "axios";
import debounce from "lodash/debounce";
import { API_URL } from "../../utils/apiUtils";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getLoanCollectionService,
  principalCollectionsService,
  updateLoanCollectionsService,
} from "../../services/loanService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function PrincipalCollection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [loan, setLoan] = useState({});
  const token = sessionStorage.getItem("accessToken");
  const date = new Date().toISOString().split("T")[0];
  const [condition, setCondition] = useState(false); // Make input read-only based on condition]
  const [isSubmit, setIsSubmit] = useState(false);

  const fetchOptions = async (query) => {
    if (!query) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/loan/search-loan`, {
        params: { query},
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setOptions(response.data.customers);
      console.log(response.data.customers);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
    setLoading(false);
  };

  const debouncedFetchOptions = useCallback(
    debounce((query) => {
      fetchOptions(query);
    }, 1000),
    []
  );
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0"); // Ensure 2 digits
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleAutocompleteChange = async (event, newValue, setFieldValue) => {
    try {
      // Reset loan-related fields if no value is selected
      if (!newValue) {
        setFieldValue("loan", "");
        setLoan(null);
        return;
      }

      // Set the loan ID
      setFieldValue("loan", newValue._id);

      // Fetch loan details
      const fetchLoan = await getLoanCollectionService(newValue._id,true);
      setLoan(fetchLoan);

      // Format the due date
      const formattedDueDate = formatDate(fetchLoan?.dueDate);

      // Set form values based on loan status
      if (fetchLoan.status === "partial") {
        const amount = fetchLoan?.collectionAmount - fetchLoan?.paidAmount;
        setFieldValue("collectionAmount", amount);
        setFieldValue("paidAmount", amount);
      } else {
        setFieldValue("collectionAmount", fetchLoan?.collectionAmount);
        setFieldValue("paidAmount", fetchLoan?.collectionAmount);
      }

      // Set remaining form values
      setFieldValue("frequency", fetchLoan?.loan?.frequency);
      if (fetchLoan?.loan?.frequency === "monthly") {
        setCondition(true);
      }
      setFieldValue("totalLoanAmount", fetchLoan?.loan?.loanAmount);
      setFieldValue("agentName", fetchLoan?.agent?.userName);
      setFieldValue("amountToBePaidBy", fetchLoan?.loan?.amountToBePaidBy);
      setFieldValue("dueDate", formattedDueDate);
      setFieldValue("interestAmount", fetchLoan?.interestAmount);
      setFieldValue(
        "totalReceivedAmount",
        fetchLoan?.loan?.totalReceivedAmount
      );
      setFieldValue("remainingAmount", fetchLoan?.loan?.remainingAmount);
      setFieldValue(
        "totalInterestPaid",
        fetchLoan?.loan?.totalInterestAmountPaid
      );

      console.log(formattedDueDate); // Ensure it's formatted properly
    } catch (error) {
      // Display an error toast message
      toast.error(error.message);
      console.error("Error fetching loan details:", error);
    }
  };
  const formik = useFormik({
    initialValues: {
      loan: "",
      agentName: "",
      frequency: "",
      totalLoanAmount: "",
      totalReceivedAmount: Number,
      remainingAmount: Number,
      collectionAmount: "",
      interestAmount: "",
      totalInterestPaid: "",
      amountToBePaidBy: "",
      dueDate: null,
      collectionDate: date,
      paidAmount: 0,
    },
    validationSchema: Yup.object({
      loan: Yup.string().required("Loan is required"),
      collectionAmount: Yup.number().required("Collection amount is required"),
      collectionDate: Yup.date().required("CollectionDate Required"),
      paidAmount: Yup.number(),
    }),
    onSubmit: async (values) => {
      try {
        console.log(values);
        setIsSubmit(true);
        const updateLoanCollection = await principalCollectionsService(
          loan?._id,
          values.amountToBePaidBy,
          values.collectionDate,
          values.interestAmount,
          values.paidAmount
        );
        toast.success("Loan collected successfully");
        navigate("/loan-collections");
      } catch (error) {
        setIsSubmit(false);
        toast.error(error.message);
      }
    },
  });

  const handleAmountPaid = async (value) => {
    if (value === "interest") {
      return formik?.values?.interestAmount || 0;
    } else if (value === "interest+emi" || value === "emi") {
      return formik?.values?.collectionAmount;
    } else if (value === "penalty") {
      return loan?.penaltyAmount;
    }
  };

  return (
    <Paper sx={{ padding: 4 }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* Customer and Loan details section */}
          <Grid item xs={12} sm={12}>
            <Typography variant="body1" gutterBottom>
              Customer Name/Loan Code/Phone Number
            </Typography>
            <Autocomplete
              value={
                options?.find((option) => option?._id === formik.values.loan) ||
                null
              }
              onChange={(event, newValue) => {
                handleAutocompleteChange(event, newValue, formik.setFieldValue);
              }}
              onInputChange={(event, newInputValue) => {
                if (event && event.type === "change") {
                  debouncedFetchOptions(newInputValue);
                }
              }}
              options={options}
              getOptionLabel={(option) =>
                `${option?.user?.userName} (${option?.loanCode}) (${option?.user?.primaryPhone?.contactValue})` ||
                ""
              }
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  error={Boolean(formik.touched.loan && formik.errors.loan)}
                  helperText={formik.touched.loan && formik.errors.loan}
                />
              )}
            />
          </Grid>

          {/* Agent Name */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Agent Name</Typography>
            <TextField
              fullWidth
              name="agentName"
              value={formik.values.agentName}
              onChange={formik.handleChange}
              size="small"
              disabled={true}
            />
          </Grid>

          {/* Frequency */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Frequency</Typography>
            <TextField
              fullWidth
              name="frequency"
              value={formik.values.frequency}
              onChange={formik.handleChange}
              size="small"
              disabled={true}
            />
          </Grid>

          {/* Loan Amount details */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Total Loan Amount</Typography>
            <TextField
              fullWidth
              name="totalLoanAmount"
              value={formik.values.totalLoanAmount}
              onChange={formik.handleChange}
              size="small"
              disabled={true}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Total Received Amount</Typography>
            <TextField
              fullWidth
              name="totalReceivedAmount"
              value={formik.values.totalReceivedAmount}
              onChange={formik.handleChange}
              size="small"
              disabled={true}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Remaining Amount</Typography>
            <TextField
              fullWidth
              name="remainingAmount"
              value={formik.values.remainingAmount}
              onChange={formik.handleChange}
              size="small"
              disabled={true}
            />
          </Grid>

          {/* Collection and Interest Information */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Collection Amount</Typography>
            <TextField
              fullWidth
              name="collectionAmount"
              disabled={true}
              value={formik.values.collectionAmount}
              onChange={formik.handleChange}
              size="small"
              error={Boolean(
                formik.touched.collectionAmount &&
                  formik.errors.collectionAmount
              )}
              helperText={
                formik.touched.collectionAmount &&
                formik.errors.collectionAmount
              }
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Interest Amount</Typography>
            <TextField
              fullWidth
              name="interestAmount"
              value={formik.values.interestAmount}
              onChange={formik.handleChange}
              size="small"
              disabled={true}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Total Interest Amount Paid</Typography>
            <TextField
              fullWidth
              name="totalInterestPaid"
              value={formik.values.totalInterestPaid}
              onChange={formik.handleChange}
              size="small"
              disabled={true}
            />
          </Grid>

          {/* <Grid item xs={12} sm={3}>
            <Typography variant="body2">Amount To Be Paid By</Typography>
            <TextField
              size="small"
              name="amountToBePaidBy"
              select
              fullWidth
              required
              variant="outlined"
              value={formik.values.amountToBePaidBy}
              onChange={async (e) => {
                formik.handleChange(e); // Make sure formik handles the field change
                const amount = await handleAmountPaid(e.target.value); // Pass the selected value to handleAmountPaid
                formik.setFieldValue("paidAmount", amount); // Set the calculated amount
              }}
            >
              {formik.values?.frequency === "monthly"
                ? [
                    <MenuItem key="interest" value="interest">
                      Interest
                    </MenuItem>,
                    <MenuItem
                      key="emi"
                      value="emi"
                      disabled={loan?.loan?.amountToBePaidBy === "interest"}
                    >
                      EMI
                    </MenuItem>,
                    // <MenuItem
                    //   key="interest+emi"
                    //   value="interest+emi"
                    //   disabled={loan?.loan?.amountToBePaidBy === "interest"}
                    // >
                    //   Interest + EMI
                    // </MenuItem>,
                  ]
                : [
                    <MenuItem key="emi" value="emi">
                      EMI
                    </MenuItem>,
                    <MenuItem key="penalty" value="penalty">
                      Penalty
                    </MenuItem>,
                  ]}
            </TextField>
          </Grid> */}

          {/* Collection Date Field */}
          {/* Collection Date Field */}
          {/* <Grid item xs={12} sm={3}>
            <Typography variant="body2">Due Date</Typography>
            <TextField
              type="date"
              name="dueDate"
              value={formik.values.dueDate || ""}
              onChange={formik.handleChange}
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={true}
            />
          </Grid> */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Collection Date</Typography>
            <TextField
              type="date"
              value={formik.values.collectionDate || ""}
              onChange={(e) => {
                formik.setFieldValue("collectionDate", e.target.value);
              }}
              fullWidth
              size="small"
              error={
                formik.touched.collectionDate &&
                Boolean(formik.errors.collectionDate)
              }
              helperText={
                formik.touched.collectionDate && formik.errors.collectionDate
              }
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                readOnly: true, // Makes the field read-only
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Principal Amount</Typography>
            <TextField
              type="number"
              fullWidth
              inputProps={{
                min: 0, // Prevent negative numbers
                step: 1, // Optional: Define step increment (if needed)
                style: { appearance: "textfield" }, // Remove arrows for most browsers
                //readOnly: condition, // Make input read-only based on condition
              }}
              sx={{
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                  {
                    appearance: "none", // Remove arrows for WebKit browsers (Chrome, Safari)
                    margin: 0,
                  },
                "& input[type=number]": {
                  MozAppearance: "textfield", // Remove arrows for Firefox
                },
              }}
              name="paidAmount"
              value={formik.values.paidAmount}
              onChange={(e) => {
                formik.setFieldValue("paidAmount", e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e") {
                  e.preventDefault(); // Prevent minus sign and "e"
                }
              }}
              size="small"
              error={Boolean(
                formik.touched.paidAmount && formik.errors.paidAmount
              )}
              helperText={formik.touched.paidAmount && formik.errors.paidAmount}
            />
          </Grid>

          {/* EMI Information Section */}
          {/* <Grid item xs={12}>
            <Typography variant="h6" color="red" gutterBottom>
              EMI Information
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>EMI Date</TableCell>
                    <TableCell>EMI Amount</TableCell>
                    <TableCell>Paid EMI Amount</TableCell>
                    <TableCell>Paid Interest Amount</TableCell>
                    <TableCell>Penalty</TableCell>
                    <TableCell>EMI Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emiInfo.map((emi, index) => (
                    <TableRow key={index}>
                      <TableCell>{emi.date}</TableCell>
                      <TableCell>{emi.emiAmount}</TableCell>
                      <TableCell>{emi.paidEMI}</TableCell>
                      <TableCell>{emi.paidInterest}</TableCell>
                      <TableCell>{emi.penalty}</TableCell>
                      <TableCell>{emi.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid> */}

          {/* Overdue EMI Information */}
          {/* <Grid item xs={12}>
            <Typography variant="h6" color="red" gutterBottom>
              Overdue EMI Information
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Overdue EMI Date</TableCell>
                    <TableCell>Paid EMI Amount</TableCell>
                    <TableCell>Paid Interest Amount</TableCell>
                    <TableCell>Penalty</TableCell>
                    <TableCell>EMI Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overdueEmiInfo.map((overdue, index) => (
                    <TableRow key={index}>
                      <TableCell>{overdue.overdueDate}</TableCell>
                      <TableCell>{overdue.paidEMI}</TableCell>
                      <TableCell>{overdue.paidInterest}</TableCell>
                      <TableCell>{overdue.penalty}</TableCell>
                      <TableCell>{overdue.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid> */}

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              sx={{ borderRadius: "8px" }}
              disabled={isSubmit}
              // onClick={handleSubmit}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}

export default PrincipalCollection;
